import React, { useState } from 'react'
import map from 'lodash/map'
import classnames from 'classnames'
import Button from '@SiteComponents/Button'
import Popup from '@SiteComponents/Popup'
import Cancel from '@Images/new_project_popup_cancel.svg'
import Continue from '@Images/setup_project_popup_styling_continue.svg'
import { safeMediaPlay } from '@Utils/SafeMediaPlay'

import {
  ITheme,
} from '@SiteModules/Theme/types'

import './SetupStylingPopup.scss'

interface SetupStylingPopupProps {
  readonly themes: ITheme[]
  readonly styleOption: string | null
  readonly shouldNotRenderOverlay?: boolean
  setShowStylingPopup(newShowStylingPopup: boolean): void
  setStyleOption(newStyleOption: string | null): void
}

const SetupStylingPopup: React.FC<SetupStylingPopupProps> = props => {
  const {
    themes,
    styleOption,
    shouldNotRenderOverlay,
  } = props

  const [selectedStyleOption, setSelectedStyleOption] = useState(styleOption || 'Default')
  const [hiddenImageIndex, setHiddenImageIndex] = useState<number | undefined>(undefined)

  const closeStylingPopup = () => {
    props.setShowStylingPopup(false)
  }

  const handleApplyStyling = () => {
    props.setStyleOption(selectedStyleOption)
    closeStylingPopup()
  }

  const renderThemeList = () => {
    return (
      <ul className="SetupStylingPopup__themes">
        {map(themes, (theme, index) => {
          const { attributes } = theme
          const { name, sampleUrlSetup, sampleImageUrlSetup } = attributes
          const isSelected = name === selectedStyleOption
          const videoId = `theme-preview-${index}`

          const handleSelectTheme = () => {
            setSelectedStyleOption(name)
          }

          const onMouseOver = () => {
            setHiddenImageIndex(index)
            const video = document.getElementById(videoId) as HTMLVideoElement
            safeMediaPlay(video, { context: 'SetupStylingPopup preview hover' })
          }

          const onMouseOut = () => {
            setHiddenImageIndex(undefined)
            const video = document.getElementById(videoId) as HTMLVideoElement
            video.pause()
            video.currentTime = 0
          }

          const shouldShowImage = hiddenImageIndex !== index

          return (
            <li
              className={classnames(
                'SetupStylingPopup__theme',
                { 'SetupStylingPopup__theme-selected': isSelected }
              )}
              onClick={handleSelectTheme}
              key={index}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
            >
              <div className="SetupStylingPopup__theme-img-video-wrapper">
                <video
                  id={videoId}
                  loop
                >
                  <source src={sampleUrlSetup} type="video/mp4" />
                </video>
                {shouldShowImage && (
                  <img src={sampleImageUrlSetup}/>
                )}
              </div>
              <div
                className="SetupStylingPopup__theme-title"
              >
                {name}
              </div>
            </li>
          )
        })}
      </ul>
    )
  }

  const renderButtons = () => {
    return (
      <div className="SetupStylingPopup__buttons">
        <Button
          caption="Cancel"
          icon={<Cancel />}
          onClick={closeStylingPopup}
          styleType="gray-subtle"
          size="small"
        />
        <Button
          caption="Apply selected style"
          icon={<Continue />}
          iconRight
          onClick={handleApplyStyling}
          styleType="brand"
          size="small"
        />
      </div>
    )
  }

  return (
    <Popup
      className="SetupStylingPopup"
      zIndex={200000}
      shouldNotRenderOverlay={shouldNotRenderOverlay}
      closePopup={closeStylingPopup}
    >
      <div className="SetupProjectPopup__header SetupStylingPopup__header">
        Choose your subtitle style
      </div>
      <div className="SetupStylingPopup__subheader">
        Choose one of our themes to give your video more impact
      </div>
      {renderThemeList()}
      {renderButtons()}
    </Popup>
  )
}

export default SetupStylingPopup
